import { Card, Skeleton } from '@parrot-software/design-system';
import { PageContent } from '@subscriptions/presentation/components/base';

/**
 * BlockDetailContentSkeleton - Loading skeleton for tab content area only
 *
 * Renders placeholder skeletons for: action buttons, cards, and table.
 * Used inside BlockDetailTabs when data is loading.
 *
 * @returns {React.ReactElement} Content skeleton layout
 */
export const BlockDetailContentSkeleton = () => (
  <div className="flex flex-col gap-4">
    {/* Action buttons skeleton */}
    <div className="flex items-center justify-end gap-4">
      <Skeleton className="h-10 w-44 rounded-md" />
      <Skeleton className="h-10 w-36 rounded-md" />
    </div>

    {/* Cards skeleton */}
    <div className="grid grid-cols-[2fr_1fr] gap-4">
      <Card className="p-6 shadow-xs">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-7 w-96" />
            <Skeleton className="h-10 w-72" />
          </div>
          <div className="flex flex-col gap-2">
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-full" />
          </div>
        </div>
      </Card>
      <Card className="p-6 shadow-xs">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-16 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </Card>
    </div>

    {/* Table skeleton */}
    <Card className="p-6 shadow-xs">
      <div className="flex flex-col gap-6">
        <Skeleton className="h-7 w-32" />
        <div className="flex flex-col gap-0 rounded-lg border overflow-hidden">
          <Skeleton className="h-10 w-full rounded-none" />
          <Skeleton className="h-12 w-full rounded-none" />
          <Skeleton className="h-12 w-full rounded-none" />
          <Skeleton className="h-12 w-full rounded-none" />
        </div>
      </div>
    </Card>
  </div>
);

/**
 * BlockDetailSkeleton - Full-page loading skeleton for the block detail page
 *
 * Renders placeholder skeletons for: breadcrumb, title, separator, tabs, and content.
 *
 * @returns {React.ReactElement} Full page skeleton layout
 */
export const BlockDetailSkeleton = () => (
  <PageContent>
    {/* Header skeleton */}
    <div className="px-6 py-4">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-48" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-6 w-96" />
          <Skeleton className="h-px w-full" />
        </div>
      </div>
    </div>

    {/* Tabs + content skeleton */}
    <div className="px-6 pt-2 pb-6">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-10 w-80 rounded-md" />
        <BlockDetailContentSkeleton />
      </div>
    </div>
  </PageContent>
);
